import template from "./template";

/**
 * Represents a single web element
 * @typedef element
 * @type {object}
 * @property {string} name
 * @property {string} text
 * @property {object} attrs
 * @property {boolean} selfClosing
 * @property {boolean} allowText
 * @property {boolean} isMetadata
 */

// Which HTML element a definition type is, with any attributes it always
// carries. An element whose children run on in its line is inLine; a text
// is a line of its own unless it or its box is styled to sit in a line. A
// link to the page shown is marked.
/** @type {Record<string, { name: string, isMetadata?: boolean, allowText?: boolean, selfClosing?: boolean, attrs?: Record<string, string>, inLine?: boolean, inLineName?: string, marksCurrent?: boolean }>} */
const elements = {
  title: { name: "title", isMetadata: true, allowText: true },
  box: { name: "div" },
  grid: { name: "div" },
  button: { name: "button", allowText: true, inLine: true },
  image: { name: "img", selfClosing: true },
  frame: {
    name: "iframe",
    attrs: { loading: "lazy", allow: "fullscreen; picture-in-picture" },
  },
  link: { name: "a", inLine: true, marksCurrent: true },
  list: { name: "ul" },
  item: { name: "li" },
  text: { name: "p", allowText: true, inLine: true, inLineName: "span" },
  "heading-level-1": { name: "h1", allowText: true, inLine: true },
  "heading-level-2": { name: "h2", allowText: true, inLine: true },
  "heading-level-3": { name: "h3", allowText: true, inLine: true },
  "heading-level-4": { name: "h4", allowText: true, inLine: true },
  "heading-level-5": { name: "h5", allowText: true, inLine: true },
  "heading-level-6": { name: "h6", allowText: true, inLine: true },
  resource: { name: "link", isMetadata: true, selfClosing: true },
  description: {
    name: "meta",
    isMetadata: true,
    selfClosing: true,
    attrs: { name: "description" },
  },
  form: {
    name: "form",
    attrs: { action: "/_stenzil/form", method: "post" },
  },
  field: { name: "input", selfClosing: true },
  "field-multiline": { name: "textarea" },
  "field-label": { name: "label", allowText: true, inLine: true },
  "field-choice": { name: "select" },
  "field-option": { name: "option", allowText: true },
};

// The attribute that marks a link to the page being shown.
const current = { name: "aria-current", value: "page" };

// Which HTML attribute a property is, and whether its value is a template.
/** @type {Record<string, { name: string, canTemplate?: boolean }>} */
const attrs = {
  styles: { name: "class", canTemplate: true },
  desc: { name: "alt", canTemplate: true },
  title: { name: "title" },
  src: { name: "src", canTemplate: true },
  addr: { name: "href", canTemplate: true },
  target: { name: "target" },
  mime: { name: "type" },
  rel: { name: "rel" },
  content: { name: "content" },
  field: { name: "name" },
  kind: { name: "type" },
  id: { name: "id" },
  for: { name: "for" },
  hint: { name: "placeholder" },
  popover: { name: "popover" },
  opens: { name: "popovertarget" },
  label: { name: "aria-label" },
};

const _web = { elements: elements, attributes: attrs };

/**
 * Where a part is drawn: the kind and styles of the part it sits in, the
 * styles of the part that one sits in, whether it is its holder's first
 * part, and the path of the page being shown
 * @typedef place
 * @type {object}
 * @property {string=} within
 * @property {string=} styles
 * @property {string=} around
 * @property {boolean=} first
 * @property {string=} here
 * @property {object=} data - the row the styles are filled from, as a list's item wears its own row's look
 * @property {object=} aroundData - the row the styles around are filled from
 */

// Display classes that draw a part within the line it sits in.
const inLineDisplays = ["inline", "inline-block", "inline-flex"];

// Display classes that lay a box's parts side by side.
const rowDisplays = ["flex", "inline-flex", "inline"];

// Classes that turn a flex box's parts into a column.
const columnClasses = ["flex-col", "flex-col-reverse"];

/**
 * The classes a part's styles name
 * @param {string | undefined} styles
 * @returns {string[]}
 */
const classesOf = (styles) => (styles ?? "").split(/\s+/).filter(Boolean);

/**
 * Whether a part's own styles draw it within the line it sits in
 * @param {string | undefined} styles
 * @returns {boolean}
 */
const drawsInLine = (styles) =>
  classesOf(styles).some((c) => inLineDisplays.includes(c));

/**
 * Whether a box's styles lay its parts side by side
 * @param {string | undefined} styles
 * @returns {boolean}
 */
const laysInRow = (styles) => {
  const classes = classesOf(styles);
  return (
    classes.some((c) => rowDisplays.includes(c)) &&
    !classes.some((c) => columnClasses.includes(c))
  );
};

/**
 * Whether a list's or item's styles put the marker inside the item's first line
 * @param {string | undefined} styles
 * @returns {boolean}
 */
const marksInside = (styles) => classesOf(styles).includes("list-inside");

/**
 * A path as a page is served at it: no # or query, no closing slash but
 * the home page's
 * @param {string} addr
 * @returns {string}
 */
const pathOf = (addr) => {
  const path = addr.split(/[?#]/)[0];
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
};

/**
 * Whether an address opens the page being shown
 * @param {string | undefined} addr
 * @param {string | undefined} here
 * @returns {boolean}
 */
const opensHere = (addr, here) =>
  !!addr &&
  !!here &&
  addr.startsWith("/") &&
  !addr.startsWith("//") &&
  pathOf(addr) === pathOf(here);

/**
 * A filled tel: link dials the number's digits alone, so a phone kept as shown, as (555) 123-4567, makes a link a phone dials
 * @param {string} written - the address as the definition writes it
 * @param {string} address - the address with its fields filled
 * @returns {string}
 */
const dialed = (written, address) =>
  written.includes("${") && /^tel:/i.test(address)
    ? "tel:" + address.slice(4).replace(/[^\d+]/g, "")
    : address;

/**
 * Maps component properties to web attributes
 * @param {import("./definition").properties} props
 * @param {object} data - the row of data a template is filled from
 * @returns {Record<string, string>}
 */
function loadAttributes(props, data) {
  /** @type {Record<string, string>} */
  var result = {};
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (key !== "txt" && value) {
        // get the attribute name from
        // the component property name
        const attr = attrs[key];
        // A grid's columns or a cell is never an attribute.
        if (attr && typeof value === "string") {
          if (attr.canTemplate) {
            result[attr.name] = dialed(value, template.substitute(value, data));
          } else {
            result[attr.name] = value;
          }
        }
      }
    }
  }
  return result;
}

/**
 * Whether a part is drawn within a line: its holder runs on in its line or
 * lays its parts in a row, its own styles put it in a line, or it opens a
 * list item whose marker sits inside
 * @param {string | undefined} styles - the part's own styles
 * @param {place} place
 * @returns {boolean}
 */
const inALine = (styles, place) => {
  const within = place.within ? _web.elements[place.within] : undefined;
  const holder = template.substitute(place.styles, place.data);
  const opensItem =
    place.within === "item" &&
    !!place.first &&
    (marksInside(holder) ||
      marksInside(template.substitute(place.around, place.aroundData)));
  return (
    !!within?.inLine || laysInRow(holder) || opensItem || drawsInLine(styles)
  );
};

/**
 * Whether parts held draw nothing: each an image with no picture, or a part
 * holding only such parts, as the frame that only holds an empty picture
 * @param {import("./definition").definition[] | undefined} parts
 * @param {object} data - the row the parts are filled from
 * @returns {boolean}
 */
const drawNothing = (parts, data) =>
  Array.isArray(parts) &&
  parts.length > 0 &&
  parts.every(
    (part) =>
      !part?.d &&
      (part?.t === "image"
        ? !loadAttributes(part.p, data).src
        : drawNothing(part?.c, data)),
  );

const web = {
  /**
   * The part shown over the page that holds an element, where the browser can close it, or nothing
   * @param {Element} element
   * @returns {HTMLElement | undefined}
   */
  overOf: (element) => {
    const over = element.parentElement?.closest(`[${attrs.popover.name}]`);
    return over instanceof HTMLElement && typeof over.hidePopover === "function"
      ? over
      : undefined;
  },
  /**
   * Maps a component to a web element with attributes, or to nothing when
   * the table has no row for the name, or when the parts it holds all draw
   * nothing
   * @param {string} name - component name
   * @param {import("./definition").properties} props - component properties
   * @param {object} data - the row of data a template is filled from
   * @param {place} [place] - where the part is drawn
   * @param {import("./definition").definition[]} [holds] - the parts it holds as written
   * @returns {element | undefined}
   */
  map: (name, props, data, place = {}, holds = undefined) => {
    const ele = _web.elements[name];

    // a kind the table does not know is nothing on the page
    if (!ele) {
      return undefined;
    }

    const tag =
      ele.inLineName && inALine(template.substitute(props?.styles, data), place)
        ? ele.inLineName
        : ele.name;

    // TODO: have the fallback be to show the text
    const txt = props && props.txt ? template.substitute(props.txt, data) : "";

    let attrs = loadAttributes(props, data);

    // A link its row gives no address is its words alone, never a link back to the page.
    if (attrs.href === "") {
      delete attrs.href;
    }

    // An image its row gives no picture is nothing on the page, never a broken picture, nor is the frame only holding it.
    if ((name === "image" && !attrs.src) || drawNothing(holds, data)) {
      return undefined;
    }

    // add any default attrs from the element to the props
    if (ele.attrs) {
      for (const [key, value] of Object.entries(ele.attrs)) {
        attrs[key] = value;
      }
    }
    if (ele.marksCurrent && opensHere(attrs.href, place.here)) {
      attrs[current.name] = current.value;
    }
    return {
      name: tag,
      text: txt,
      attrs: attrs,
      selfClosing: ele.selfClosing ?? false,
      allowText: ele.allowText ?? false,
      isMetadata: ele.isMetadata ?? false,
    };
  },
};

export default web;
